import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';

import type { GroupSelection } from './groupFilter';
import { useBulkSetArmed } from '../../api/useEntry';
import {
  applyBulkArmedDtos,
  buildBulkArmedDtos,
  type BulkArmedSelection
} from './bulkScope';

/**
 * BULK arm/disarm for the opener tab — "Arm all" (green) / "Disarm all" (red)
 * across a WHOLE scope in one request via `/rules/bulk-armed`. Mirrors
 * `GroupMakerBulkControls` (same scope-aware shape + confirm-dialog UX), but
 * flips the rules' explicit `desiredArmed` boolean rather than a maker flag —
 * `desiredArmed` is now the ONLY arm/disarm trigger.
 *
 * BOTH actions route through a confirm dialog: arming starts real trading,
 * disarming stops it. The control is scope-aware: a GROUP scope (a real group →
 * scope:'all' + groupId) or a concrete SUBGROUP scope (scope:'all' +
 * subgroupId), and selection-aware: when a checkbox selection is live it scopes
 * to the checked rows only (scope:'ids' over their rule ids), exactly like the
 * PM/KM maker bulk. It is DISABLED unless a concrete scope exists (a real group,
 * a subgroup, or a live selection) — never arms the whole account from the
 * 'all' / 'ungrouped' filters, mirroring the unwinder platform bulk.
 */

export type GroupArmScope =
  | {
      kind: 'group';
      /** The page's group filter — all / a real group / ungrouped. */
      selection: GroupSelection;
      label: string | null;
      /** Group-scoped (pre-search) rule ids — only used on the Ungrouped path. */
      scopedRuleIds: string[];
    }
  | {
      kind: 'subgroup';
      subgroupId: string;
      label: string;
    };

interface GroupArmBulkControlsProps {
  scope: GroupArmScope;
  /** Honest pair total for the confirm dialog (the whole scope, not just the
   *  search-narrowed rows). */
  confirmCount: number;
  /** Rule ids behind the currently checked rows. When `selectedCount > 0` the
   *  control switches to SELECTION mode (scope:'ids' over these ids), exactly
   *  like the PM/KM maker bulk — group scope is ignored while a selection is
   *  live. */
  selectedRuleIds?: string[];
  selectedCount?: number;
}

export function GroupArmBulkControls({
  scope,
  confirmCount,
  selectedRuleIds = [],
  selectedCount = 0
}: GroupArmBulkControlsProps) {
  const bulkArmed = useBulkSetArmed();
  const [confirm, setConfirm] = useState<{ armed: boolean } | null>(null);

  // Selection mode mirrors the PM/KM maker bulk: a live checkbox selection
  // takes over the Arm/Disarm buttons and scopes them to the checked rows only.
  const selectionMode = selectedCount > 0;

  // Concrete scope guard: only a real group, a subgroup, or a live selection
  // may arm/disarm. The 'all' / 'ungrouped' filters never arm the whole
  // account (mirror the unwinder platform bulk).
  const concreteScope =
    scope.kind === 'subgroup' ||
    (scope.kind === 'group' && scope.selection.kind === 'group');
  const canBulk = selectionMode || concreteScope;

  const labelSx = {
    fontSize: '0.62rem',
    color: 'grey.500',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap' as const
  };
  const btnSx = { fontSize: '0.62rem', py: 0.1, px: 0.8, minWidth: 0 } as const;

  const run = useCallback(
    async (armed: boolean) => {
      const selection: BulkArmedSelection = selectionMode
        ? { kind: 'selected', ruleIds: selectedRuleIds }
        : scope.kind === 'subgroup'
          ? { kind: 'subgroup', subgroupId: scope.subgroupId }
          : scope.selection;
      const scopedRuleIds =
        !selectionMode && scope.kind === 'group' ? scope.scopedRuleIds : [];
      const dtos = buildBulkArmedDtos(selection, armed, scopedRuleIds);
      if (dtos.length === 0) return;
      await applyBulkArmedDtos(dtos, dto => bulkArmed.mutateAsync(dto));
    },
    [scope, bulkArmed, selectionMode, selectedRuleIds]
  );

  // Honest count + noun for the confirm dialog — the checked subset when a
  // selection is live, otherwise the whole group/subgroup scope.
  const effectiveCount = selectionMode ? selectedCount : confirmCount;
  const scopeNoun = selectionMode
    ? 'the selected pairs'
    : scope.kind === 'subgroup'
      ? scope.label
      : scope.label ?? 'this group';

  return (
    <>
      {bulkArmed.isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <CircularProgress size={13} thickness={5} />
          <Typography sx={{ ...labelSx, color: 'grey.300' }}>
            Applying — arm/disarm…
          </Typography>
        </Box>
      ) : (
        <>
          {selectionMode && (
            <Typography sx={{ ...labelSx, color: 'grey.300' }}>
              {selectedCount} selected
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              color="success"
              variant="outlined"
              sx={btnSx}
              disabled={bulkArmed.isLoading || !canBulk}
              onClick={() => setConfirm({ armed: true })}
            >
              Arm all
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              sx={btnSx}
              disabled={bulkArmed.isLoading || !canBulk}
              onClick={() => setConfirm({ armed: false })}
            >
              Disarm all
            </Button>
          </Box>
        </>
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle sx={{ fontSize: '0.95rem' }}>
          {confirm?.armed ? 'Arm' : 'Disarm'} {scopeNoun}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            This {confirm?.armed ? 'arms' : 'disarms'} {effectiveCount} pair
            {effectiveCount === 1 ? '' : 's'} in {scopeNoun}.
            {confirm?.armed
              ? ' Arming ACTIVATES the rules and starts placing REAL orders.'
              : ' Disarming pauses the rules and stops new orders.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            color={confirm?.armed ? 'success' : 'error'}
            onClick={() => {
              if (confirm) void run(confirm.armed);
              setConfirm(null);
            }}
            autoFocus
          >
            {confirm?.armed ? 'Arm' : 'Disarm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
